import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function AssessmentsSkeleton() {
  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="space-y-2">
          <div className="h-8 w-48 bg-muted rounded animate-pulse"></div>
          <div className="h-4 w-64 bg-muted rounded animate-pulse"></div>
        </div>
        <div className="h-10 w-40 bg-muted rounded animate-pulse"></div>
      </div>
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="h-10 flex-1 bg-muted rounded animate-pulse"></div>
            <div className="h-10 w-36 bg-muted rounded animate-pulse"></div>
            <div className="h-10 w-36 bg-muted rounded animate-pulse"></div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="p-4 animate-pulse">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="h-5 bg-muted rounded w-2/5"></div>
                    <div className="flex gap-3">
                      <div className="h-4 bg-muted rounded w-24"></div>
                      <div className="h-4 bg-muted rounded w-32"></div>
                      <div className="h-4 bg-muted rounded w-20"></div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="h-6 w-24 bg-muted rounded"></div>
                    <div className="h-8 w-8 bg-muted rounded"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Plus, Search, Eye, Edit, Check, X, Trash2, ClipboardCheck, Building, Calendar, User, FileText, Clock, CheckCircle, XCircle, Grid3X3, List, ArrowUpDown, UserCheck, AlertCircle, Zap } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { hasAnyRole } from "@/lib/authUtils";
import { Link } from "wouter";
import React from "react"; // Import React for useEffect
import { useLocation } from "wouter"; // Import useLocation

import AssessmentForm from "@/components/assessment/assessment-form";
import { ReassignDialog } from "@/components/ui/reassign-dialog";
import { ReviewStatusBadge } from "@/components/assessment/review-status-badge";
import { useUserTimezone } from "@/hooks/useUserTimezone";

export default function Assessments() {
  const { user } = useAuth();
  const [location] = useLocation();
  const { toast } = useToast();
  const { formatDateOnly } = useUserTimezone();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [assignedToFilter, setAssignedToFilter] = useState("all");
  const [reviewFilter, setReviewFilter] = useState("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState<any>(null);
  const [deleteAssessmentId, setDeleteAssessmentId] = useState<number | null>(null);
  const [preselectedCustomerId, setPreselectedCustomerId] = useState<string | null>(null);

  // Check URL parameters on component mount
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldCreate = urlParams.get('create');
    const customerId = urlParams.get('customer');

    if (shouldCreate === 'true') {
      setShowCreateForm(true);
      if (customerId) {
        setPreselectedCustomerId(customerId);
      }
      // Clean up URL parameters
      window.history.replaceState({}, '', '/assessments/technical-alignment');
    }
  }, [location]);

  const { data: assessments, isLoading } = useQuery<any[]>({
    queryKey: ["/api/assessments"],
  });

  const { data: customers } = useQuery<any[]>({
    queryKey: ["/api/customers"],
  });

  const { data: templates } = useQuery<any>({
    queryKey: ["/api/templates"],
  });

  const { data: users } = useQuery<any[]>({
    queryKey: ["/api/users/list"],
  });

  const { data: qbrs } = useQuery<any[]>({
    queryKey: ["/api/qbrs"],
  });

  // Helper function to calculate days until due (matches backend logic)
  const calculateDaysUntilDue = (nextReviewDate: string | Date | null): number | null => {
    if (!nextReviewDate) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day

    const reviewDate = new Date(nextReviewDate);
    reviewDate.setHours(0, 0, 0, 0); // Normalize to start of day

    const diffTime = reviewDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  const approveAssessmentMutation = useMutation({
    mutationFn: async (assessmentId: number) => {
      await apiRequest("PATCH", `/api/assessments/${assessmentId}`, {
        status: "approved",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/assessments"] });
      toast({
        title: "Success",
        description: "Assessment approved successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to approve assessment",
        variant: "destructive",
      });
    },
  });

  const deleteAssessmentMutation = useMutation({
    mutationFn: async (assessmentId: number) => {
      await apiRequest("DELETE", `/api/assessments/${assessmentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/assessments"] });
      setDeleteAssessmentId(null);
      toast({
        title: "Success",
        description: "Assessment deleted successfully",
      });
    },
    onError: (error: any) => {
      const errorMessage = error?.message || "Failed to delete assessment";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Check if an assessment is ready for QBR generation (approved and no QBR assigned)
  const isReadyForQbr = (assessment: any) => {
    if (assessment.status !== 'approved') return false;
    const isAssignedToQbr = qbrs?.some((qbr: any) => qbr.assessmentId === assessment.id);
    return !isAssignedToQbr;
  };

  const getStatusBadge = (status: string, assessment?: any) => {
    const statusConfig: { [key: string]: { label: string; variant: string; color: string } } = {
      draft: { label: "Draft", variant: "secondary", color: "text-gray-600" },
      in_progress: { label: "In Progress", variant: "outline", color: "text-blue-600 border-blue-600" },
      pending_review: { label: "Pending Review", variant: "outline", color: "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-950/50 dark:text-orange-300 dark:border-orange-700" },
      approved: { label: "Approved", variant: "outline", color: "text-green-600 border-green-600" },
      completed: { label: "Completed", variant: "outline", color: "text-purple-600 border-purple-600" },
    };

    const config = statusConfig[status] || statusConfig.draft;
    const readyForQbr = assessment && isReadyForQbr(assessment);

    return (
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant={config.variant as any} className={config.color}>
          {config.label}
        </Badge>
        {readyForQbr && (
          <Badge 
            variant="outline" 
            className="text-amber-600 border-amber-500 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-600 dark:text-amber-400 text-xs"
            title="Ready to generate Business Review"
          >
            <Zap className="h-3 w-3 mr-1" />
            Create BR
          </Badge>
        )}
      </div>
    );
  };

  const getCustomerName = (customerId: number) => {
    const customer = customers?.find((c: any) => c.id === customerId);
    return customer?.name || `Customer ${customerId}`;
  };

  const canReassignAssessment = (assessment: any) => {
    if (!user) return false;

    // Creator can always reassign
    if (assessment.createdById === user.id) return true;

    // vCIO+ roles can reassign
    if (hasAnyRole(user, ['vcio', 'admin', 'owner'])) return true;

    return false;
  };

  const getAssignedUserName = (assignedToId: string) => {
    const assignedUser = users?.find((u: any) => u.id === assignedToId);
    return assignedUser ? `${assignedUser.firstName} ${assignedUser.lastName}` : "Unknown User";
  };

  // Helper function to get the current assignee based on assessment status
  const getCurrentAssignee = (assessment: any) => {
    const { status, assignedToId, vcioId } = assessment;
    
    // Backward compatibility: if no vcioId, use assignedToId for both roles
    const effectiveVcioId = vcioId || assignedToId;
    
    if (status === 'draft' || status === 'in_progress') {
      // Show Alignment Engineer
      return {
        userId: assignedToId,
        role: 'AE',
        displayName: getAssignedUserName(assignedToId)
      };
    } else if (status === 'pending_review') {
      // Show vCIO
      return {
        userId: effectiveVcioId,
        role: 'vCIO',
        displayName: getAssignedUserName(effectiveVcioId)
      };
    } else {
      // approved or completed - show both roles
      const aeName = getAssignedUserName(assignedToId);
      const vcioName = getAssignedUserName(effectiveVcioId);
      
      // If same person, show once
      if (assignedToId === effectiveVcioId) {
        return {
          userId: assignedToId,
          role: 'Both',
          displayName: aeName
        };
      }
      
      return {
        userId: [assignedToId, effectiveVcioId],
        role: 'Both',
        displayName: `${aeName} (AE) / ${vcioName} (vCIO)`
      };
    }
  };

  const filteredAssessments = assessments?.filter((assessment: any) => {
    const matchesSearch = getCustomerName(assessment.customerId)
      .toLowerCase()
      .includes(searchTerm.toLowerCase()) ||
      assessment.title.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "all" || assessment.status === statusFilter;

    // Updated to include assessments where user is either Alignment Engineer or vCIO
    const matchesAssignedTo = assignedToFilter === "all" || 
      assessment.assignedToId === assignedToFilter || 
      assessment.vcioId === assignedToFilter;

    // Review filter
    const matchesReview = reviewFilter === "all" || 
      (reviewFilter === "due" && assessment.nextReviewDate && new Date(assessment.nextReviewDate) <= new Date()) ||
      (reviewFilter === "upcoming" && assessment.nextReviewDate && new Date(assessment.nextReviewDate) > new Date());

    return matchesSearch && matchesStatus && matchesAssignedTo && matchesReview;
  }).sort((a: any, b: any) => {
    let aValue, bValue;

    switch (sortBy) {
      case "title":
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
        break;
      case "customer":
        aValue = getCustomerName(a.customerId).toLowerCase();
        bValue = getCustomerName(b.customerId).toLowerCase();
        break;
      case "status":
        aValue = a.status;
        bValue = b.status;
        break;
      case "createdAt":
      default:
        aValue = new Date(a.createdAt);
        bValue = new Date(b.createdAt);
        break;
    }

    if (sortOrder === "asc") {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  }) || [];

  const handleDeleteAssessment = (assessmentId: number) => {
    setDeleteAssessmentId(assessmentId);
  };

  const handleEditAssessment = async (assessment: any) => {
    try {
      // Check if assessment has associated QBRs
      const hasQbrs = await apiRequest("GET", `/api/assessments/${assessment.id}/has-qbrs`);

      if (hasQbrs) {
        toast({
          title: "Cannot Edit Assessment",
          description: "This assessment has associated Business Reviews and cannot be edited. Please delete the Business Reviews first.",
          variant: "destructive",
        });
        return;
      }

      setEditingAssessment(assessment);
      setShowEditForm(true);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to check assessment status";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const confirmDelete = () => {
    if (deleteAssessmentId) {
      deleteAssessmentMutation.mutate(deleteAssessmentId);
    }
  };

  // Get approved assessments count for QBR generation notification
  const approvedAssessmentsCount = assessments?.filter((assessment: any) => 
    assessment.status === "approved" && 
    !qbrs?.some((qbr: any) => qbr.assessmentId === assessment.id)
  ).length || 0;

  // Loading state - must be after all hooks
  if (isLoading) {
    return (
      <div>
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/4 mb-8"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-lg font-bold text-foreground flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6 md:h-8 md:w-8" />
            Assessments
          </h1>
          <p className="text-muted-foreground">Manage and track your technical alignment assessments</p>
        </div>

        <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
          <DialogTrigger asChild>
            <Button data-onboarding="create-assessment">
              <Plus className="h-4 w-4 mr-2" />
              Create Assessment
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Assessment</DialogTitle>
              <DialogDescription>
                Create a new assessment for a customer
              </DialogDescription>
            </DialogHeader>
            <AssessmentForm 
              customers={customers} 
              templates={templates}
              preselectedCustomerId={preselectedCustomerId}
              onSuccess={() => {
                setShowCreateForm(false);
                setPreselectedCustomerId(null);
              }}
              onCancel={() => {
                setShowCreateForm(false);
                setPreselectedCustomerId(null);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Approved Assessments Notification - Removed as redundant with sidebar badges and inline "Create BR" buttons */}
      {/* The sidebar already shows counts and each approved assessment row has a "Create BR" action button */}
      {false && approvedAssessmentsCount > 0 && (
        <Alert className="mb-4 border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
          <AlertCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            <div>
              <strong>{approvedAssessmentsCount} approved assessment{approvedAssessmentsCount > 1 ? 's' : ''}</strong> {approvedAssessmentsCount > 1 ? 'are' : 'is'} ready for Business Review generation.
              <span className="ml-2">Navigate to the assessment details to generate Business Reviews from approved assessments.</span>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Filters and Controls */}
      <Card className="mb-4" data-onboarding="assessment-filters">
        <CardContent className="p-4 md:p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
            {/* Search */}
            <div className="flex-1 relative">
              <Input
                placeholder="Search assessments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="pending_review">Pending Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>

            {/* Assigned To Filter */}
            <Select value={assignedToFilter} onValueChange={setAssignedToFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Assigned to" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {users?.map((user: any) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Review Filter */}
            <Select value={reviewFilter} onValueChange={setReviewFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="All Reviews" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Reviews</SelectItem>
                <SelectItem value="due">Due for Review</SelectItem>
                <SelectItem value="upcoming">Upcoming Review</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort By */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="createdAt">Date Created</SelectItem>
                <SelectItem value="title">Title</SelectItem>
                <SelectItem value="customer">Customer</SelectItem>
                <SelectItem value="status">Status</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort Order */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              className="w-20"
            >
              <ArrowUpDown className="h-4 w-4 mr-1" />
              {sortBy === "createdAt" 
                ? (sortOrder === "asc" ? "Old" : "New")
                : (sortOrder === "asc" ? "A-Z" : "Z-A")
              }
            </Button>

            {/* View Toggle */}
            <div className="flex border rounded-md">
              <Button
                variant={viewMode === "grid" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("grid")}
                className="rounded-r-none"
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
                className="rounded-l-none"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Assessments Display */}
      <Card data-onboarding="assessments-list">
        <CardHeader>
          <CardTitle>All Assessments</CardTitle>
        </CardHeader>

        <CardContent className="p-0">
          {viewMode === "grid" ? (
            // Grid View
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
              {filteredAssessments.length > 0 ? (
                filteredAssessments.map((assessment: any) => (
                  <Card key={assessment.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="w-12 h-12 bg-blue-50 dark:bg-blue-950 rounded-lg flex items-center justify-center flex-shrink-0">
                            <ClipboardCheck className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 
                              className="font-semibold text-foreground truncate cursor-pointer hover:text-primary transition-colors"
                              onClick={() => window.location.href = `/assessments/${assessment.id}`}
                            >
                              {assessment.title}
                            </h3>
                            <div data-onboarding="assessment-status">
                              {getStatusBadge(assessment.status, assessment)}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3 mb-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Customer:</span>
                          <span className="text-foreground font-medium">{getCustomerName(assessment.customerId)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Alignment Engineer:</span>
                          <span className="text-foreground">{getAssignedUserName(assessment.assignedToId)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">vCIO:</span>
                          <span className="text-foreground">{assessment.vcioId ? getAssignedUserName(assessment.vcioId) : 'Not assigned'}</span>
                        </div>

                        {assessment.nextReviewDate && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Review:</span>
                            <ReviewStatusBadge
                              nextReviewDate={new Date(assessment.nextReviewDate)}
                              isOverdue={assessment.isOverdueForReview || false}
                              daysUntilDue={calculateDaysUntilDue(assessment.nextReviewDate)}
                              lastReviewedAt={assessment.lastReviewedAt ? new Date(assessment.lastReviewedAt) : undefined}
                              reviewCount={assessment.reviewCount || 0}
                            />
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditAssessment(assessment)}
                          className="flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {assessment.status === "pending_review" && hasAnyRole(user, ['vcio', 'admin']) && (
                          <Link href={`/assessments/${assessment.id}/review`} className="flex-1">
                            <Button variant="outline" size="sm" className="w-full text-blue-600 hover:text-blue-800">
                              <ClipboardCheck className="h-4 w-4 mr-2" />
                              Review
                            </Button>
                          </Link>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteAssessment(assessment.id)}
                          className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Created {formatDateOnly(assessment.createdAt)}</span>
                          <span>ID: {assessment.id}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="col-span-full text-center py-6">
                  <ClipboardCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No assessments found</h3>
                  <p className="text-muted-foreground mb-4">
                    {searchTerm || statusFilter !== "all" 
                      ? "Try adjusting your search or filter criteria."
                      : "Start by creating your first assessment."
                    }
                  </p>
                </div>
              )}
            </div>
          ) : (
            // List View
            <div>
              {/* Mobile Cards - Show on small screens */}
              <div className="block lg:hidden p-4">
                {filteredAssessments.length > 0 ? (
                  <div className="space-y-3">
                    {filteredAssessments.map((assessment: any) => (
                      <div key={assessment.id} className="p-4 bg-muted/30 rounded-lg border">
                        <div className="flex items-start justify-between mb-3">
                          <div className="min-w-0 flex-1">
                            <Link href={`/assessments/${assessment.id}`}>
                              <h3 className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer hover:underline truncate">
                                {assessment.title}
                              </h3>
                            </Link>
                            <p className="text-sm text-muted-foreground">ID: {assessment.id}</p>
                          </div>
                          <div className="ml-3 flex-shrink-0" data-onboarding="assessment-status">
                            {getStatusBadge(assessment.status, assessment)}
                          </div>
                        </div>

                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Customer:</span>
                            <span className="text-foreground">{getCustomerName(assessment.customerId)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Alignment Engineer:</span>
                            <span className="text-foreground">{getAssignedUserName(assessment.assignedToId)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">vCIO:</span>
                            <span className="text-foreground">{assessment.vcioId ? getAssignedUserName(assessment.vcioId) : 'Not assigned'}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Created:</span>
                            <span className="text-foreground">{formatDateOnly(assessment.createdAt)}</span>
                          </div>
                          {assessment.nextReviewDate && (
                            <div className="flex justify-between text-sm items-center">
                              <span className="text-muted-foreground">Review:</span>
                              <ReviewStatusBadge
                                nextReviewDate={new Date(assessment.nextReviewDate)}
                                isOverdue={assessment.isOverdueForReview || false}
                                daysUntilDue={calculateDaysUntilDue(assessment.nextReviewDate)}
                                lastReviewedAt={assessment.lastReviewedAt ? new Date(assessment.lastReviewedAt) : undefined}
                                reviewCount={assessment.reviewCount || 0}
                              />
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditAssessment(assessment)}
                            className="flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                            title="Edit Assessment"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {canReassignAssessment(assessment) && (
                            <ReassignDialog
                              type="assessment"
                              itemId={assessment.id}
                              itemTitle={assessment.title}
                              currentAssigneeId={assessment.assignedToId}
                              currentAssigneeName={getAssignedUserName(assessment.assignedToId)}
                            >
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                title="Reassign Assessment"
                              >
                                <UserCheck className="h-4 w-4" />
                              </Button>
                            </ReassignDialog>
                          )}
                          {assessment.status === "pending_review" && hasAnyRole(user, ['vcio', 'admin']) && (
                            <Link href={`/assessments/${assessment.id}/review`} className="flex-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full text-blue-600 hover:text-blue-800"
                                title="Review Assessment"
                              >
                                <ClipboardCheck className="h-4 w-4 mr-2" />
                                Review
                              </Button>
                            </Link>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteAssessment(assessment.id)}
                            title="Delete Assessment"
                            className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <ClipboardCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">No assessments found</h3>
                    <p className="text-muted-foreground">
                      {searchTerm || statusFilter !== "all" 
                        ? "Try adjusting your search or filter criteria."
                        : "Start by creating your first assessment."
                      }
                    </p>
                  </div>
                )}
              </div>

              {/* Desktop Table - Show on large screens */}
              <div className="hidden lg:block">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-background">
                      <tr>
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => {
                            if (sortBy === "title") {
                              setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                            } else {
                              setSortBy("title");
                              setSortOrder("asc");
                            }
                          }}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Assessment</span>
                            <ArrowUpDown className="h-3 w-3" />
                            {sortBy === "title" && (
                              <span className="text-primary">
                                {sortOrder === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => {
                            if (sortBy === "customer") {
                              setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                            } else {
                              setSortBy("customer");
                              setSortOrder("asc");
                            }
                          }}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Customer</span>
                            <ArrowUpDown className="h-3 w-3" />
                            {sortBy === "customer" && (
                              <span className="text-primary">
                                {sortOrder === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors" 
                          data-onboarding="status-header"
                          onClick={() => {
                            if (sortBy === "status") {
                              setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                            } else {
                              setSortBy("status");
                              setSortOrder("asc");
                            }
                          }}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Status</span>
                            <ArrowUpDown className="h-3 w-3" />
                            {sortBy === "status" && (
                              <span className="text-primary">
                                {sortOrder === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Alignment Engineer
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          vCIO
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Next Review
                        </th>
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => {
                            if (sortBy === "createdAt") {
                              setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                            } else {
                              setSortBy("createdAt");
                              setSortOrder("desc");
                            }
                          }}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Created</span>
                            <ArrowUpDown className="h-3 w-3" />
                            {sortBy === "createdAt" && (
                              <span className="text-primary">
                                {sortOrder === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-gray-200">
                      {filteredAssessments.length > 0 ? (
                        filteredAssessments.map((assessment: any) => (
                          <tr key={assessment.id}>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div>
                                <Link href={`/assessments/${assessment.id}`}>
                                  <div className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer hover:underline">
                                    {assessment.title}
                                  </div>
                                </Link>
                                <div className="text-sm text-muted-foreground">ID: {assessment.id}</div>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-sm text-foreground">{getCustomerName(assessment.customerId)}</div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap" data-onboarding="assessment-status">
                              {getStatusBadge(assessment.status, assessment)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground">
                              {getAssignedUserName(assessment.assignedToId)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground">
                              {assessment.vcioId ? getAssignedUserName(assessment.vcioId) : <span className="text-muted-foreground">Not assigned</span>}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              {assessment.nextReviewDate ? (
                                <ReviewStatusBadge
                                  nextReviewDate={new Date(assessment.nextReviewDate)}
                                  isOverdue={assessment.isOverdueForReview || false}
                                  daysUntilDue={calculateDaysUntilDue(assessment.nextReviewDate)}
                                  lastReviewedAt={assessment.lastReviewedAt ? new Date(assessment.lastReviewedAt) : undefined}
                                  reviewCount={assessment.reviewCount || 0}
                                />
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground">
                              {formatDateOnly(assessment.createdAt)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-muted-foreground">
                              <div className="flex items-center space-x-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditAssessment(assessment)}
                                  title="Edit Assessment"
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                {canReassignAssessment(assessment) && (
                                  <ReassignDialog
                                    type="assessment"
                                    itemId={assessment.id}
                                    itemTitle={assessment.title}
                                    currentAssigneeId={assessment.assignedToId}
                                    currentAssigneeName={getAssignedUserName(assessment.assignedToId)}
                                  >
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                      title="Reassign Assessment"
                                    >
                                      <UserCheck className="h-4 w-4" />
                                    </Button>
                                  </ReassignDialog>
                                )}
                                {assessment.status === "pending_review" && hasAnyRole(user, ['vcio', 'admin']) && (
                                  <Link href={`/assessments/${assessment.id}/review`}>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      title="Review Assessment"
                                      className="text-blue-600 hover:text-blue-800"
                                    >
                                      <ClipboardCheck className="h-4 w-4" />
                                    </Button>
                                  </Link>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteAssessment(assessment.id)}
                                  title="Delete Assessment"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="px-4 py-4 text-center text-sm text-muted-foreground">
                            No assessments found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Assessment Dialog */}
      <Dialog open={showEditForm} onOpenChange={(open) => {
        setShowEditForm(open);
        if (!open) {
          setEditingAssessment(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Assessment</DialogTitle>
          </DialogHeader>
          {editingAssessment && (
            <AssessmentForm
              mode="edit"
              assessment={editingAssessment}
              onSuccess={() => {
                setShowEditForm(false);
                setEditingAssessment(null);
                queryClient.invalidateQueries({ queryKey: ["/api/assessments"] });
                toast({
                  title: "Success",
                  description: "Assessment updated successfully",
                });
              }}
              onCancel={() => {
                setShowEditForm(false);
                setEditingAssessment(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteAssessmentId !== null} onOpenChange={() => setDeleteAssessmentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Assessment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this assessment? This action cannot be undone.
              All responses and progress will be permanently lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteAssessmentId(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteAssessmentMutation.isPending}
            >
              {deleteAssessmentMutation.isPending ? "Deleting..." : "Delete Assessment"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}